import { Col, Container, Form, Row } from "react-bootstrap";
import { Controller, useFormContext } from "react-hook-form";
import Select from "../../../../components/AppSelect";
import {
  customStyles,
  REGEX,
  UNIT_DIMENSIONS_OPTIONS,
  UNIT_WEIGHT_OPTIONS,
} from "../../utils/utils";
import { useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { useProductForm } from "../productModalForm";

const defaultValues = {
  unitWeight: "",
  weight: null,
  unitDimensions: "",
  width: "",
  height: "",
  length: "",
};

export const ShippingDimensionsFormComponent = ({
  rowEdited = null,
  shippers = {},
}) => {
  const { isFM, disabled: formDisabled } = useProductForm();

  const {
    control,
    register,
    formState: { errors },
    watch,
    setValue
  } = useFormContext({
    defaultValues: defaultValues,
  });

  const shipperId = watch("shipperId");

  const selectedShipper = useMemo(() => {
    if (!shipperId || !shippers?.items) return null;
    return shippers.items.find((s) => s.idCompany === shipperId) ?? null;
  }, [shipperId, shippers]);

  const unitWeightOptions = useMemo(() => {
    const units = selectedShipper?.countryData?.countryConfig?.weightUnit;
    if (!units?.length) return UNIT_WEIGHT_OPTIONS;
    const allowed = new Set(units.map((u) => u.toUpperCase()));
    const filtered = UNIT_WEIGHT_OPTIONS.filter((opt) => allowed.has(opt.value));
    return filtered.length ? filtered : UNIT_WEIGHT_OPTIONS;
  }, [selectedShipper]);

  const unitDimensionsOptions = useMemo(() => {
    const units = selectedShipper?.countryData?.countryConfig?.measurementUnit;
    if (!units?.length) return UNIT_DIMENSIONS_OPTIONS;
    const allowed = new Set(units.map((u) => u.toUpperCase()));
    const filtered = UNIT_DIMENSIONS_OPTIONS.filter((opt) => allowed.has(opt.value));
    return filtered.length ? filtered : UNIT_DIMENSIONS_OPTIONS;
  }, [selectedShipper]);

  const isFirstRender = useRef(true);

  const requiredMessage = (field) =>
    isFM ? `Es obligatorio seleccionar ${field}` : false;

  const numericRequiredMessage = (field) =>
    isFM ? `El ${field} es obligatorio` : false;

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (rowEdited && shipperId === rowEdited.idCompany) return;
    setValue("unitWeight", "");
    setValue("weight", null);
    setValue("unitDimensions", "");
    setValue("width", "");
    setValue("height", "");
    setValue("length", "");
  }, [shipperId]);

  return (
    <Container>
      <h4>Dimensiones y peso</h4>
      <hr />
      <Row>
        <Form.Group as={Col} className="px-3 pb-2">
          <label htmlFor="unitWeight" className="form-label">
            Unidad de peso{isFM && <span className="text-danger">*</span>}
          </label>
          <Controller
            name="unitWeight"
            control={control}
            rules={{
              required: requiredMessage("una unidad de peso"),
            }}
            render={({ field }) => (
              <Select
                {...field}
                placeholder="Seleccionar"
                className={`${!!errors?.unitWeight && "is-invalid"}`}
                styles={customStyles}
                options={unitWeightOptions}
                isDisabled={formDisabled}
                value={
                  field.value
                    ? unitWeightOptions.find(
                        (option) => option.value === field.value,
                      )
                    : null
                }
                onChange={(selectedOption) => {
                  field.onChange(selectedOption ? selectedOption.value : null);
                  if (rowEdited) setValue("weight", "");
                }}
              />
            )}
          />
          {!!errors?.unitWeight && (
            <span className="error-message">
              {errors?.unitWeight?.message || "Este campo es obligatorio"}
            </span>
          )}
        </Form.Group>
        <Form.Group as={Col} className="px-3 pb-2">
          <label htmlFor="weight" className="form-label">
            Peso{isFM && <span className="text-danger">*</span>}
          </label>
          <input
            {...register("weight", {
              required: numericRequiredMessage("peso"),
              pattern: {
                value: REGEX.NUMERIC_WITH_DOC,
                message: "Ingrese un número válido en formato 0.00",
              },
              validate: (value) => {
                if (!isFM) return true;
                return parseFloat(value) > 0 || "El valor debe ser mayor que 0";
              },
            })}
            id="weight"
            type="text"
            step="any"
            placeholder="0.00"
            className={`form-control ${!!errors?.weight && "is-invalid"}`}
            disabled={formDisabled || !watch("unitWeight")}
          />
          {!!errors?.weight && (
            <span className="error-message">
              {errors?.weight?.message || "Este campo es obligatorio"}
            </span>
          )}
        </Form.Group>
        <Form.Group as={Col} className="px-3 pb-2">
          <label htmlFor="unitDimensions" className="form-label">
            Unidad (dimensiones){isFM && <span className="text-danger">*</span>}
          </label>
          <Controller
            name="unitDimensions"
            control={control}
            rules={{
              required: requiredMessage("una unidad (dimensiones)"),
            }}
            render={({ field }) => (
              <Select
                {...field}
                placeholder="Seleccionar"
                className={`${!!errors?.unitDimensions && "is-invalid"}`}
                styles={customStyles}
                options={unitDimensionsOptions}
                isDisabled={formDisabled}
                value={
                  field.value
                    ? unitDimensionsOptions.find(
                        (option) => option.value === field.value,
                      )
                    : null
                }
                onChange={(selectedOption) => {
                  field.onChange(selectedOption ? selectedOption.value : null);
                  if (rowEdited) {
                    setValue("width", "");
                    setValue("height", "");
                    setValue("length", "");
                  }
                }}
              />
            )}
          />
          {!!errors?.unitDimensions && (
            <span className="error-message">
              {errors?.unitDimensions?.message || "Este campo es obligatorio"}
            </span>
          )}
        </Form.Group>
      </Row>
      <Row>
        <Form.Group as={Col} className="px-3 pb-2">
          <label htmlFor="width" className="form-label">
            Ancho{isFM && <span className="text-danger">*</span>}
          </label>
          <input
            {...register("width", {
              required: numericRequiredMessage("ancho"),
              pattern: {
                value: REGEX.NUMERIC_WITH_DOC,
                message: "Ingrese un número válido en formato 0.00",
              },
              validate: (value) => {
                if (!isFM) return true;
                return parseFloat(value) > 0 || "El valor debe ser mayor que 0";
              },
            })}
            id="width"
            type="text"
            step="any"
            placeholder="0.00"
            className={`form-control ${!!errors?.width && "is-invalid"}`}
            disabled={formDisabled || !watch("unitDimensions")}
          />
          {!!errors?.width && (
            <span className="error-message">
              {errors?.width?.message || "Este campo es obligatorio"}
            </span>
          )}
        </Form.Group>
        <Form.Group as={Col} className="px-3 pb-2">
          <label htmlFor="height" className="form-label">
            Alto{isFM && <span className="text-danger">*</span>}
          </label>
          <input
            {...register("height", {
              required: numericRequiredMessage("alto"),
              pattern: {
                value: REGEX.NUMERIC_WITH_DOC,
                message: "Ingrese un número válido en formato 0.00",
              },
              validate: (value) => {
                if (!isFM) return true;
                return parseFloat(value) > 0 || "El valor debe ser mayor que 0";
              },
            })}
            id="height"
            type="text"
            step="any"
            placeholder="0.00"
            className={`form-control ${!!errors?.height && "is-invalid"}`}
            disabled={formDisabled || !watch("unitDimensions")}
          />
          {!!errors?.height && (
            <span className="error-message">
              {errors?.height?.message || "Este campo es obligatorio"}
            </span>
          )}
        </Form.Group>
        <Form.Group as={Col} className="px-3 pb-2">
          <label htmlFor="length" className="form-label">
            Largo{isFM && <span className="text-danger">*</span>}
          </label>
          <input
            {...register("length", {
              required: numericRequiredMessage("largo"),
              pattern: {
                value: REGEX.NUMERIC_WITH_DOC,
                message: "Ingrese un número válido en formato 0.00",
              },
              validate: (value) => {
                if (!isFM) return true;
                return parseFloat(value) > 0 || "El valor debe ser mayor que 0";
              },
            })}
            id="length"
            type="text"
            step="any"
            placeholder="0.00"
            className={`form-control ${!!errors?.length && "is-invalid"}`}
            disabled={formDisabled || !watch("unitDimensions")}
          />
          {!!errors?.length && (
            <span className="error-message">
              {errors?.length?.message || "Este campo es obligatorio"}
            </span>
          )}
        </Form.Group>
      </Row>
    </Container>
  );
};

const mapStateToProps = (state) => {
  return {
    rowEdited: state.productState.rowEdited,
    shippers: state.companyState.clients,
  };
};

export const ShippingDimensionsForm = connect(
  mapStateToProps,
)(ShippingDimensionsFormComponent);
